import {
  PButton,
  PButtonPure,
  PInputNumber,
} from '@porsche-design-system/components-react';
import { Column, FilterFn, RowData } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { ActionGroup, Popover, Spacer } from '../..';
import type { FilterTableFeatures } from '../table-features';

export type NumberRangeFilterValue = [number | undefined, number | undefined];

export const inNumberRangeFilterFn: FilterFn<any, any> = (
  row,
  columnId,
  value,
) => {
  if (!value || (!Array.isArray(value) && typeof value !== 'object')) {
    return true;
  }

  const [min, max] = value as [number | undefined, number | undefined];

  const raw = row.getValue(columnId);
  if (raw === null || raw === undefined || raw === '' || raw === false) {
    return false;
  }

  const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
  if (isNaN(num)) {
    return false;
  }

  if (min !== undefined && min !== null && num < min) {
    return false;
  }
  if (max !== undefined && max !== null && num > max) {
    return false;
  }

  return true;
};

export const NumberRangeFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
>({
  column,
  i18n,
}: {
  column: Column<TFeatures, T, unknown>;
  i18n: {
    filterLabel: string;
    buttonReset: string;
    buttonFilter: string;
    minLabel: string;
    maxLabel: string;
  };
}) => {
  const [isVisible, setVisibility] = useState(false);
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const filterValue = filterColumn.getFilterValue() as
    | NumberRangeFilterValue
    | undefined;

  const [minVal, setMinVal] = useState<number | undefined>(filterValue?.[0]);
  const [maxVal, setMaxVal] = useState<number | undefined>(filterValue?.[1]);

  useEffect(() => {
    setMinVal(filterValue?.[0]);
    setMaxVal(filterValue?.[1]);
  }, [filterValue]);

  const hasFilter =
    filterValue?.[0] !== undefined || filterValue?.[1] !== undefined;

  return (
    <Popover
      target={
        <PButtonPure
          type="button"
          active={hasFilter}
          size="xs"
          icon="filter"
          onClick={() => setVisibility((value) => !value)}
          hideLabel
        >
          {i18n.filterLabel}
        </PButtonPure>
      }
      open={isVisible}
      onChange={setVisibility}
      placement="right-start"
      withPortal
    >
      <div>
        <PInputNumber
          name="min"
          label={i18n.minLabel}
          value={minVal !== undefined ? String(minVal) : ''}
          onInput={(e) => {
            const val = (e.target as HTMLInputElement).value;
            setMinVal(
              val !== '' && !isNaN(Number(val)) ? Number(val) : undefined,
            );
          }}
        />
        <Spacer mb="sm" />
        <PInputNumber
          name="max"
          label={i18n.maxLabel}
          value={maxVal !== undefined ? String(maxVal) : ''}
          onInput={(e) => {
            const val = (e.target as HTMLInputElement).value;
            setMaxVal(
              val !== '' && !isNaN(Number(val)) ? Number(val) : undefined,
            );
          }}
        />
        <Spacer mb="md" />
        <ActionGroup spacing="md">
          <PButton
            type="button"
            variant="secondary"
            icon="close"
            onClick={() => {
              setMinVal(undefined);
              setMaxVal(undefined);
              filterColumn.setFilterValue(undefined);
              setVisibility(false);
            }}
          >
            {i18n.buttonReset}
          </PButton>
          <PButton
            type="button"
            onClick={() => {
              if (minVal !== undefined || maxVal !== undefined) {
                filterColumn.setFilterValue([minVal, maxVal]);
              } else {
                filterColumn.setFilterValue(undefined);
              }
              setVisibility(false);
            }}
          >
            {i18n.buttonFilter}
          </PButton>
        </ActionGroup>
      </div>
    </Popover>
  );
};
